import * as THREE from 'three';
import { roundedBox, cylinder, toon } from './toon.js';

const TILE_SIZE=2.25;
const TILE_GAP=.11;
const TILE_HEIGHT=.48;
const RUPTURE_LAYOUTS={
  'rupture-void':{
    colors:['#39d9ee','#ffd83d','#ff7893','#a679ff','#4df29b','#ff6b45'],
    accents:['#167f9e','#d17b28','#a83763','#5636a7','#18865d','#a52d38'],
    stages:[
      // 17 × 13 bloques de 2.25 unidades: aproximadamente 38 × 29,
      // la mitad de las dimensiones lineales del Living actual (80 × 60).
      {center:[0,14,0],shape:()=>rectangle(8,6),name:'PLATAFORMA INICIAL'},
      {center:[38,17.2,-34],shape:()=>roundedSquare(6),name:'EL BALCÓN'},
      {center:[5,20.8,-72],shape:()=>ring(6,2),name:'EL ANILLO'},
      {center:[-39,24.5,-39],shape:()=>cross(6,2),name:'LA CRUZ'},
      {center:[-50,28.4,8],shape:()=>twins(),name:'LAS GEMELAS'},
      {center:[-5,32.5,45],shape:()=>square(6),name:'ÚLTIMO PISO'},
    ],
  },
  'rupture-storm':{
    colors:['#5cf2dc','#ffbd3f','#7d8cff','#ff6f91','#43d8ff','#f8ea64'],
    accents:['#157d82','#b55e22','#3944a5','#a4295b','#147596','#9d7d16'],
    stages:[
      {center:[0,13,0],shape:()=>stormLaunchPad(),name:'PISTA DE DESPEGUE'},
      {center:[-42,17,30],shape:()=>turbine(),name:'LA TURBINA'},
      {center:[-7,21.5,70],shape:()=>cloudChain(),name:'NUBES EN CADENA'},
      {center:[42,26,42],shape:()=>stormEye(),name:'OJO DE LA TORMENTA'},
      {center:[53,31,-14],shape:()=>lightningBolt(),name:'EL RELÁMPAGO'},
      {center:[11,36,-52],shape:()=>stormCore(),name:'NÚCLEO FINAL'},
    ],
  },
};

function square(radius){
  const coordinates=[];
  for(let x=-radius;x<=radius;x+=1){
    for(let z=-radius;z<=radius;z+=1)coordinates.push([x,z]);
  }
  return coordinates;
}

function rectangle(radiusX,radiusZ){
  const coordinates=[];
  for(let x=-radiusX;x<=radiusX;x+=1){
    for(let z=-radiusZ;z<=radiusZ;z+=1)coordinates.push([x,z]);
  }
  return coordinates;
}

function roundedSquare(radius){
  return square(radius).filter(([x,z])=>Math.abs(x)+Math.abs(z)<radius*2);
}

function ring(radius,holeRadius=1){
  return square(radius).filter(([x,z])=>Math.max(Math.abs(x),Math.abs(z))>holeRadius);
}

function cross(radius,armWidth=1){
  return square(radius).filter(([x,z])=>Math.abs(x)<=armWidth||Math.abs(z)<=armWidth);
}

function twins(){
  const coordinates=[];
  for(const centerX of [-4,4]){
    for(let x=-2;x<=2;x+=1){
      for(let z=-2;z<=2;z+=1)coordinates.push([centerX+x,z]);
    }
  }
  coordinates.push([-1,0],[0,0],[1,0]);
  return coordinates;
}

function uniqueCoordinates(coordinates){
  return [...new Map(coordinates.map(([x,z])=>[`${x}:${z}`,[x,z]])).values()];
}

function stormLaunchPad(){
  const coordinates=[];
  for(let x=-7;x<=7;x+=1){
    for(let z=-5;z<=5;z+=1){
      if(Math.abs(x)+Math.abs(z)<=11)coordinates.push([x,z]);
    }
  }
  for(let z=-8;z<=-6;z+=1){
    const halfWidth=z===-8?1:2;
    for(let x=-halfWidth;x<=halfWidth;x+=1)coordinates.push([x,z]);
  }
  for(const side of [-1,1]){
    for(let x=8;x<=9;x+=1){
      for(let z=-2;z<=2;z+=1)coordinates.push([side*x,z]);
    }
  }
  return uniqueCoordinates(coordinates);
}

function turbine(){
  const coordinates=square(2);
  for(let arm=0;arm<4;arm+=1){
    for(let length=2;length<=7;length+=1){
      const bend=Math.floor((length-2)*.55);
      for(let width=-2;width<=1;width+=1){
        const x=length;
        const z=bend+width;
        if(arm===0)coordinates.push([x,z]);
        else if(arm===1)coordinates.push([-z,x]);
        else if(arm===2)coordinates.push([-x,-z]);
        else coordinates.push([z,-x]);
      }
    }
  }
  return uniqueCoordinates(coordinates);
}

function cloudChain(){
  const coordinates=[];
  const lobes=[
    {x:-5,z:-1,radius:4},
    {x:0,z:2,radius:4},
    {x:5,z:0,radius:4},
  ];
  for(let x=-9;x<=9;x+=1){
    for(let z=-5;z<=6;z+=1){
      if(lobes.some((lobe)=>(x-lobe.x)**2+(z-lobe.z)**2<=lobe.radius**2)){
        coordinates.push([x,z]);
      }
    }
  }
  return coordinates;
}

function stormEye(){
  const coordinates=[];
  for(let x=-9;x<=9;x+=1){
    for(let z=-5;z<=5;z+=1){
      const body=Math.abs(x)/9+Math.abs(z)/5<=1;
      const pupil=Math.abs(x)<=2&&Math.abs(z)<=3;
      const eyelid=Math.abs(z)<=1&&Math.abs(x)<=8;
      if(body||pupil||eyelid)coordinates.push([x,z]);
    }
  }
  return coordinates;
}

function lightningBolt(){
  const coordinates=[];
  const centerForRow=(z)=>{
    if(z<=-4)return -4;
    if(z<=0)return -1;
    if(z<=4)return 3;
    return 0;
  };
  for(let z=-7;z<=7;z+=1){
    const center=centerForRow(z);
    for(let x=center-2;x<=center+2;x+=1)coordinates.push([x,z]);
    if([-4,1,5].includes(z)){
      const previous=centerForRow(z-1);
      for(let x=Math.min(previous,center)-2;x<=Math.max(previous,center)+2;x+=1){
        coordinates.push([x,z]);
      }
    }
  }
  return uniqueCoordinates(coordinates);
}

function stormCore(){
  const coordinates=[];
  for(let x=-8;x<=8;x+=1){
    for(let z=-8;z<=8;z+=1){
      const diamond=Math.abs(x)+Math.abs(z)<=6;
      const horizontal=Math.abs(z)<=1&&Math.abs(x)<=8;
      const vertical=Math.abs(x)<=1&&Math.abs(z)<=8;
      const diagonalA=Math.abs(x-z)<=1&&Math.abs(x)<=6&&Math.abs(z)<=6;
      const diagonalB=Math.abs(x+z)<=1&&Math.abs(x)<=6&&Math.abs(z)<=6;
      if(diamond||horizontal||vertical||diagonalA||diagonalB)coordinates.push([x,z]);
    }
  }
  return coordinates;
}

function phaseCopy(phase){
  return {
    id:phase.id,
    label:phase.label,
    remaining:Math.max(0,phase.remaining),
    duration:phase.duration,
  };
}

export class RuptureArena {
  constructor(scene,world,rules={},options={}){
    this.scene=scene;
    this.world=world;
    this.visual=options.visual!==false;
    this.mapId=options.mapId||world?.id||'rupture-void';
    this.layout=RUPTURE_LAYOUTS[this.mapId]||RUPTURE_LAYOUTS['rupture-void'];
    this.rules={
      combatDuration:10,
      warningDuration:2,
      transferDuration:2,
      collapseDuration:1.8,
      settleDuration:1,
      powerupRefreshDelay:5,
      finalWarningDuration:3,
      finalRingDuration:1.45,
      ...rules,
    };
    this.root=new THREE.Group();
    this.root.name=`rupture-turbo-arena-${this.mapId}`;
    this.scene.add(this.root);
    this.stages=[];
    this.fallingTiles=[];
    this.events=[];
    this.currentIndex=0;
    this.nextIndex=null;
    this.collapseOrder=[];
    this.collapseCursor=0;
    this.finalRings=[];
    this.finalRingClock=0;
    this.elapsed=0;
    this.routeMarker=null;
    this.tileGeometry=this.visual
      ?roundedBox(
        TILE_SIZE-TILE_GAP,
        TILE_HEIGHT,
        TILE_SIZE-TILE_GAP,
        '#ffffff',
        .08,
        2,
      ).geometry
      :null;
    this.matrixComposer=new THREE.Object3D();
    this.phase={
      id:'combat',
      label:'PELEÁ',
      remaining:this.rules.combatDuration,
      duration:this.rules.combatDuration,
    };
    this.createStages();
    this.activateStage(0);
  }

  createStages(){
    const definitions=this.layout.stages;
    definitions.forEach((definition,index)=>{
      const stage={
        index,
        name:definition.name,
        center:new THREE.Vector3(...definition.center),
        top:definition.center[1],
        group:new THREE.Group(),
        tiles:[],
        batches:[],
        visualsReady:false,
        collisionObstacles:[],
        active:false,
        revealed:false,
      };
      stage.group.name=`rupture-platform-${index+1}`;
      stage.group.visible=false;
      this.root.add(stage.group);
      definition.shape().forEach(([gridX,gridZ],tileIndex)=>{
        const color=(gridX+gridZ+tileIndex)%3===0
          ?this.layout.accents[index%this.layout.accents.length]
          :this.layout.colors[index%this.layout.colors.length];
        const position=new THREE.Vector3(
          stage.center.x+gridX*TILE_SIZE,
          stage.top-TILE_HEIGHT*.5,
          stage.center.z+gridZ*TILE_SIZE,
        );
        const obstacle={
          shape:'box',
          minX:position.x-(TILE_SIZE-TILE_GAP)*.5,
          maxX:position.x+(TILE_SIZE-TILE_GAP)*.5,
          minZ:position.z-(TILE_SIZE-TILE_GAP)*.5,
          maxZ:position.z+(TILE_SIZE-TILE_GAP)*.5,
          minY:stage.top-TILE_HEIGHT,
          maxY:stage.top,
          tag:'rupture-platform',
          ruptureStage:index,
        };
        stage.tiles.push({
          index:tileIndex,
          position,
          color,
          material:null,
          instanceMesh:null,
          instanceIndex:-1,
          fallMesh:null,
          obstacle,
          gridX,
          gridZ,
          collapsed:false,
          fallVelocity:0,
          fallSpin:new THREE.Vector3(),
          initialPosition:position.clone(),
        });
      });
      this.stages.push(stage);
    });
  }

  ensureStageVisual(stage){
    if(!this.visual||!stage||stage.visualsReady)return;
    const tilesByColor=new Map();
    stage.tiles.forEach((tile)=>{
      if(!tilesByColor.has(tile.color))tilesByColor.set(tile.color,[]);
      tilesByColor.get(tile.color).push(tile);
    });
    tilesByColor.forEach((tiles,color)=>{
      const material=toon(color);
      const batch=new THREE.InstancedMesh(this.tileGeometry,material,tiles.length);
      batch.name=`rupture-platform-${stage.index+1}-${color}`;
      batch.castShadow=true;
      batch.receiveShadow=true;
      batch.userData.noShadow=false;
      batch.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      tiles.forEach((tile,instanceIndex)=>{
        tile.material=material;
        tile.instanceMesh=batch;
        tile.instanceIndex=instanceIndex;
        this.setTileInstanceVisible(tile,!tile.collapsed,false);
      });
      batch.instanceMatrix.needsUpdate=true;
      batch.computeBoundingSphere();
      stage.batches.push(batch);
      stage.group.add(batch);
    });
    stage.visualsReady=true;
  }

  setTileInstanceVisible(tile,visible,update=true){
    if(!tile?.instanceMesh||tile.instanceIndex<0)return;
    this.matrixComposer.position.copy(tile.initialPosition);
    this.matrixComposer.rotation.set(0,0,0);
    this.matrixComposer.scale.setScalar(visible?1:0);
    this.matrixComposer.updateMatrix();
    tile.instanceMesh.setMatrixAt(tile.instanceIndex,this.matrixComposer.matrix);
    if(update)tile.instanceMesh.instanceMatrix.needsUpdate=true;
  }

  createFallingTile(tile){
    if(!this.visual||tile.fallMesh)return;
    const mesh=new THREE.Mesh(this.tileGeometry,tile.material||toon(tile.color));
    mesh.position.copy(tile.initialPosition);
    mesh.castShadow=true;
    mesh.receiveShadow=true;
    mesh.userData.noShadow=false;
    this.root.add(mesh);
    tile.fallMesh=mesh;
  }

  removeFallingTileVisual(tile){
    if(!tile?.fallMesh)return;
    this.root.remove(tile.fallMesh);
    tile.fallMesh=null;
  }

  refreshStageCollisions(stage){
    if(!stage)return;
    stage.collisionObstacles.forEach((obstacle)=>{
      const obstacleIndex=this.world.obstacles.indexOf(obstacle);
      if(obstacleIndex>=0)this.world.obstacles.splice(obstacleIndex,1);
    });
    stage.collisionObstacles=[];
    if(!stage.active)return;

    // Las baldosas siguen siendo visualmente independientes, pero agrupamos
    // su superficie física en tramos contiguos. La plataforma inicial pasa
    // de 221 colliders a sólo 13, conservando los huecos derrumbados.
    const rows=new Map();
    stage.tiles.forEach((tile)=>{
      if(tile.collapsed)return;
      if(!rows.has(tile.gridZ))rows.set(tile.gridZ,[]);
      rows.get(tile.gridZ).push(tile);
    });
    rows.forEach((tiles)=>{
      tiles.sort((first,second)=>first.gridX-second.gridX);
      let run=[];
      const commitRun=()=>{
        if(!run.length)return;
        const first=run[0].obstacle;
        const last=run.at(-1).obstacle;
        stage.collisionObstacles.push({
          shape:'box',
          minX:first.minX,
          maxX:last.maxX,
          minZ:first.minZ,
          maxZ:first.maxZ,
          minY:first.minY,
          maxY:first.maxY,
          tag:'rupture-platform',
          ruptureStage:stage.index,
          // Una plataforma suspendida aporta apoyo y techo, pero no debe
          // comportarse como una pared lateral capaz de atrapar el auto.
          vehicleSideCollision:false,
        });
        run=[];
      };
      tiles.forEach((tile)=>{
        if(run.length&&tile.gridX!==run.at(-1).gridX+1)commitRun();
        run.push(tile);
      });
      commitRun();
    });
    this.world.obstacles.push(...stage.collisionObstacles);
  }

  activateStage(index){
    const stage=this.stages[index];
    if(!stage||stage.active)return;
    stage.active=true;
    stage.revealed=true;
    this.ensureStageVisual(stage);
    stage.group.visible=this.visual;
    this.refreshStageCollisions(stage);
  }

  removeTile(stage,tile,impulse=0,refreshCollisions=true){
    if(!stage||!tile||tile.collapsed)return;
    tile.collapsed=true;
    tile.fallVelocity=-.5-Math.random()*.8-impulse;
    tile.fallSpin.set(
      (Math.random()-.5)*2.8,
      (Math.random()-.5)*2.2,
      (Math.random()-.5)*2.8,
    );
    this.setTileInstanceVisible(tile,false);
    this.createFallingTile(tile);
    if(this.visual)this.fallingTiles.push(tile);
    if(refreshCollisions)this.refreshStageCollisions(stage);
  }

  collapseStage(index){
    const stage=this.stages[index];
    if(!stage)return;
    this.collapseOrder=[...stage.tiles].sort(()=>Math.random()-.5);
    this.collapseCursor=0;
  }

  removeStageRemainder(index){
    const stage=this.stages[index];
    if(!stage)return;
    stage.tiles.forEach((tile)=>this.removeTile(stage,tile,.35,false));
    stage.active=false;
    stage.group.visible=false;
    this.refreshStageCollisions(stage);
  }

  revealNext(){
    this.nextIndex=this.currentIndex+1;
    this.activateStage(this.nextIndex);
    this.createRouteMarker();
    this.events.push({type:'reveal',platformIndex:this.nextIndex});
    this.setPhase('warning','¡NUEVO PISO!',this.rules.warningDuration);
  }

  createRouteMarker(){
    this.clearRouteMarker();
    if(!this.visual)return;
    const next=this.stages[this.nextIndex];
    if(!next)return;
    const marker=new THREE.Group();
    const ring=new THREE.Mesh(
      new THREE.TorusGeometry(1.15,.095,10,36),
      new THREE.MeshBasicMaterial({
        color:'#fff16a',
        transparent:true,
        opacity:.94,
        depthWrite:false,
      }),
    );
    ring.rotation.x=Math.PI/2;
    const beam=cylinder(.055,.13,3.6,'#fff16a',10);
    beam.position.y=1.8;
    beam.material=beam.material.clone();
    beam.material.transparent=true;
    beam.material.opacity=.48;
    marker.add(ring,beam);
    marker.position.copy(next.center);
    marker.position.y=next.top+.12;
    this.root.add(marker);
    this.routeMarker=marker;
  }

  clearRouteMarker(){
    if(this.routeMarker){
      this.root.remove(this.routeMarker);
      this.routeMarker.traverse((child)=>{
        if(child.isMesh&&child.material?.isMaterial&&!child.material.isMeshToonMaterial)child.material.dispose();
      });
      this.routeMarker=null;
    }
  }

  setPhase(id,label,duration){
    this.phase={id,label,remaining:duration,duration};
    this.events.push({type:'phase',phase:id,label});
  }

  beginCurrentCollapse(){
    this.collapseStage(this.currentIndex);
    this.setPhase('collapse','¡SE DERRUMBA!',this.rules.collapseDuration);
    this.events.push({type:'collapse',platformIndex:this.currentIndex});
  }

  settleOnNext(){
    this.removeStageRemainder(this.currentIndex);
    this.currentIndex=this.nextIndex;
    this.nextIndex=null;
    this.clearRouteMarker();
    this.setPhase('settle','ATERRIZAJE',this.rules.settleDuration);
    this.events.push({type:'stage',platformIndex:this.currentIndex});
  }

  beginFinalWarning(){
    this.clearRouteMarker();
    this.setPhase('final-warning','¡ÚLTIMO PISO!',this.rules.finalWarningDuration);
    this.events.push({type:'final-warning',platformIndex:this.currentIndex});
  }

  beginFinalCollapse(){
    const stage=this.stages[this.currentIndex];
    const rings=new Map();
    stage.tiles.filter((tile)=>!tile.collapsed).forEach((tile)=>{
      const distance=Math.max(Math.abs(tile.gridX),Math.abs(tile.gridZ));
      if(!rings.has(distance))rings.set(distance,[]);
      rings.get(distance).push(tile);
    });
    this.finalRings=[...rings.entries()]
      .sort((first,second)=>second[0]-first[0])
      .map(([,tiles])=>tiles.sort(()=>Math.random()-.5));
    this.finalRingClock=.35;
    const duration=.35+this.finalRings.length*this.rules.finalRingDuration;
    this.setPhase('final-collapse','COLAPSO FINAL',duration);
    this.events.push({type:'final-collapse',platformIndex:this.currentIndex});
  }

  updateFallingTiles(dt){
    for(let index=this.fallingTiles.length-1;index>=0;index-=1){
      const tile=this.fallingTiles[index];
      tile.fallVelocity-=11.5*dt;
      if(!tile.fallMesh){
        this.fallingTiles.splice(index,1);
        continue;
      }
      tile.fallMesh.position.y+=tile.fallVelocity*dt;
      tile.fallMesh.rotation.x+=tile.fallSpin.x*dt;
      tile.fallMesh.rotation.y+=tile.fallSpin.y*dt;
      tile.fallMesh.rotation.z+=tile.fallSpin.z*dt;
      if(tile.fallMesh.position.y>-8)continue;
      this.removeFallingTileVisual(tile);
      this.fallingTiles.splice(index,1);
    }
  }

  updateVisual(dt){
    this.elapsed+=dt;
    this.updateFallingTiles(dt);
    if(this.routeMarker){
      const pulse=1+Math.sin(this.elapsed*6)*.1;
      this.routeMarker.scale.setScalar(pulse);
      this.routeMarker.rotation.y+=dt*.7;
    }
  }

  update(dt){
    this.updateVisual(dt);
    if(this.phase.id==='void')return;
    this.phase.remaining=Math.max(0,this.phase.remaining-dt);

    if(this.phase.id==='collapse'){
      const stage=this.stages[this.currentIndex];
      const progress=1-this.phase.remaining/Math.max(.001,this.phase.duration);
      const target=Math.min(this.collapseOrder.length,Math.ceil(progress*this.collapseOrder.length));
      let collisionsChanged=false;
      while(this.collapseCursor<target){
        this.removeTile(stage,this.collapseOrder[this.collapseCursor],progress*.65,false);
        this.collapseCursor+=1;
        collisionsChanged=true;
      }
      if(collisionsChanged)this.refreshStageCollisions(stage);
    }else if(this.phase.id==='final-collapse'){
      this.finalRingClock-=dt;
      if(this.finalRingClock<=0&&this.finalRings.length){
        const ringTiles=this.finalRings.shift();
        const stage=this.stages[this.currentIndex];
        ringTiles.forEach((tile)=>this.removeTile(stage,tile,.9,false));
        this.refreshStageCollisions(stage);
        this.finalRingClock=this.rules.finalRingDuration;
        this.events.push({type:'final-ring',ringsRemaining:this.finalRings.length});
      }
      if(!this.finalRings.length&&this.finalRingClock<=0){
        const stage=this.stages[this.currentIndex];
        stage.active=false;
        this.refreshStageCollisions(stage);
        this.setPhase('void','SIN PISO',0);
        this.events.push({type:'void'});
        return;
      }
    }

    if(this.phase.remaining>0)return;
    switch(this.phase.id){
      case 'combat':
        if(this.currentIndex>=this.stages.length-1)this.beginFinalWarning();
        else this.revealNext();
        break;
      case 'warning':
        this.setPhase('transfer','¡SALTÁ AHORA!',this.rules.transferDuration);
        break;
      case 'transfer':
        this.beginCurrentCollapse();
        break;
      case 'collapse':
        this.settleOnNext();
        break;
      case 'settle':
        this.setPhase('combat','PELEÁ',this.rules.combatDuration);
        break;
      case 'final-warning':
        this.beginFinalCollapse();
        break;
      default:
        break;
    }
  }

  consumeEvents(){
    const events=this.events;
    this.events=[];
    return events;
  }

  destinationIndex(){
    return this.nextIndex??this.currentIndex;
  }

  getStage(index=this.currentIndex){
    return this.stages[index]||null;
  }

  activeTiles(index=this.currentIndex){
    return (this.stages[index]?.tiles||[]).filter((tile)=>!tile.collapsed);
  }

  tileAtPosition(position,index=this.currentIndex,margin=.16){
    return this.activeTiles(index).find((tile)=>
      position.x>=tile.obstacle.minX-margin
      &&position.x<=tile.obstacle.maxX+margin
      &&position.z>=tile.obstacle.minZ-margin
      &&position.z<=tile.obstacle.maxZ+margin
    )||null;
  }

  isSafePosition(position,index=this.currentIndex,clearance=1.35){
    if(!this.tileAtPosition(position,index,.12))return false;
    for(let sample=0;sample<8;sample+=1){
      const angle=sample/8*Math.PI*2;
      const probe={
        x:position.x+Math.cos(angle)*clearance,
        y:position.y,
        z:position.z+Math.sin(angle)*clearance,
      };
      if(!this.tileAtPosition(probe,index,.12))return false;
    }
    return true;
  }

  tileSupportScore(tile,index=this.currentIndex,coordinates=null){
    const occupied=coordinates||new Set(this.activeTiles(index).map((candidate)=>
      `${candidate.gridX}:${candidate.gridZ}`
    ));
    let score=0;
    for(let dx=-1;dx<=1;dx+=1){
      for(let dz=-1;dz<=1;dz+=1){
        if((dx||dz)&&occupied.has(`${tile.gridX+dx}:${tile.gridZ+dz}`))score+=1;
      }
    }
    return score;
  }

  platformAt(position){
    for(const index of [this.currentIndex,this.nextIndex]){
      if(index===null||index===undefined)continue;
      const stage=this.stages[index];
      if(!stage?.active)continue;
      if(position.y<stage.top-.22||position.y>stage.top+.4)continue;
      if(this.tileAtPosition(position,index,.12))return index;
    }
    return null;
  }

  spawnFor(slot=0,index=this.destinationIndex()){
    const stage=this.stages[index]||this.stages[0];
    const desiredOffsets=[
      [0,0],[-8,0],[8,0],[-4,6],[4,6],[0,-6],
    ];
    const desired=desiredOffsets[slot%desiredOffsets.length];
    const available=this.activeTiles(index);
    const coordinates=new Set(available.map((tile)=>`${tile.gridX}:${tile.gridZ}`));
    const safety=new Map(available.map((tile)=>[
      tile,
      this.tileSupportScore(tile,index,coordinates),
    ]));
    const protectedTiles=available.filter((tile)=>
      safety.get(tile)>=5&&this.isSafePosition(tile.position,index,1.4)
    );
    const candidates=protectedTiles.length?protectedTiles:available;
    const chosen=[...candidates].sort((first,second)=>{
      const firstDistance=(first.position.x-(stage.center.x+desired[0]))**2
        +(first.position.z-(stage.center.z+desired[1]))**2;
      const secondDistance=(second.position.x-(stage.center.x+desired[0]))**2
        +(second.position.z-(stage.center.z+desired[1]))**2;
      const firstSafety=safety.get(first)||0;
      const secondSafety=safety.get(second)||0;
      return firstDistance-secondDistance-(firstSafety-secondSafety)*1.6;
    })[0];
    const position=chosen
      ?new THREE.Vector3(chosen.position.x,stage.top+.025,chosen.position.z)
      :stage.center.clone().setY(stage.top+.025);
    const next=this.stages[Math.min(index+1,this.stages.length-1)];
    const direction=next.center.clone().sub(position);
    return {
      position,
      yaw:index<this.stages.length-1?Math.atan2(-direction.x,-direction.z):Math.PI,
    };
  }

  botTarget(slot=0,index=this.destinationIndex()){
    return this.spawnFor((slot+2)%6,index).position;
  }

  killHeight(){
    const active=[this.currentIndex,this.nextIndex]
      .filter((index)=>index!==null&&index!==undefined&&this.stages[index]?.active)
      .map((index)=>this.stages[index].top);
    const reference=active.length?Math.min(...active):this.stages[this.currentIndex].top;
    // Una caída deja tiempo real para corregir con nitro. El mapa del vacío
    // desactiva su suelo físico, así que este umbral puede estar bajo y seguir
    // sin crear una superficie invisible donde aterrizar.
    return reference-16;
  }

  state(){
    const current=this.stages[this.currentIndex];
    return {
      phase:phaseCopy(this.phase),
      platformIndex:this.currentIndex,
      platformCount:this.stages.length,
      platformName:current?.name||'—',
      nextPlatformIndex:this.nextIndex,
      nextPlatformName:this.nextIndex!==null?this.stages[this.nextIndex]?.name:null,
      final:this.phase.id.startsWith('final')||this.phase.id==='void',
      suddenDeath:this.phase.id==='final-collapse'||this.phase.id==='void',
      killHeight:this.killHeight(),
    };
  }

  networkState(){
    return {
      ...this.state(),
      activePlatforms:this.stages
        .filter((stage)=>stage.active)
        .map((stage)=>({
          index:stage.index,
          collapsed:stage.tiles
            .filter((tile)=>tile.collapsed)
            .map((tile)=>tile.index),
        })),
    };
  }

  restoreTile(stage,tile,refreshCollisions=true){
    const fallingIndex=this.fallingTiles.indexOf(tile);
    if(fallingIndex>=0)this.fallingTiles.splice(fallingIndex,1);
    tile.collapsed=false;
    tile.fallVelocity=0;
    tile.fallSpin.set(0,0,0);
    this.removeFallingTileVisual(tile);
    this.setTileInstanceVisible(tile,true);
    if(refreshCollisions)this.refreshStageCollisions(stage);
  }

  applyAuthoritativeState(state){
    if(!state||!Array.isArray(state.activePlatforms))return;
    const active=new Map(state.activePlatforms.map((platform)=>[
      platform.index,
      new Set(platform.collapsed||[]),
    ]));
    this.currentIndex=state.platformIndex;
    this.nextIndex=state.nextPlatformIndex??null;
    this.phase={
      id:state.phase?.id||this.phase.id,
      label:state.phase?.label||this.phase.label,
      remaining:Math.max(0,Number(state.phase?.remaining)||0),
      duration:Math.max(.001,Number(state.phase?.duration)||.001),
    };

    this.stages.forEach((stage)=>{
      const collapsed=active.get(stage.index);
      const isPast=stage.index<this.currentIndex;
      const isActive=Boolean(collapsed);
      let collisionsChanged=stage.active!==isActive;
      stage.active=isActive;
      stage.revealed=isPast||isActive;
      if(stage.revealed)this.ensureStageVisual(stage);
      // Las piezas que ya cayeron viven temporalmente en la raíz del modo.
      // El lote estable de una etapa pasada puede ocultarse por completo.
      stage.group.visible=this.visual&&isActive;
      stage.tiles.forEach((tile)=>{
        const currentVoid=stage.index===this.currentIndex&&this.phase.id==='void';
        const shouldCollapse=isPast||currentVoid||(isActive&&collapsed.has(tile.index));
        if(shouldCollapse&&!tile.collapsed){
          this.removeTile(stage,tile,isPast?.5:0,false);
          collisionsChanged=true;
        }else if(!shouldCollapse&&tile.collapsed){
          this.restoreTile(stage,tile,false);
          collisionsChanged=true;
        }
      });
      if(collisionsChanged)this.refreshStageCollisions(stage);
    });

    if(this.nextIndex!==null){
      if(!this.routeMarker)this.createRouteMarker();
    }else this.clearRouteMarker();
  }

  dispose(){
    this.clearRouteMarker();
    this.stages.forEach((stage)=>{
      stage.collisionObstacles.forEach((obstacle)=>{
        const obstacleIndex=this.world.obstacles.indexOf(obstacle);
        if(obstacleIndex>=0)this.world.obstacles.splice(obstacleIndex,1);
      });
      stage.tiles.forEach((tile)=>{
        this.removeFallingTileVisual(tile);
      });
    });
    this.tileGeometry?.dispose?.();
    this.scene?.remove?.(this.root);
    this.stages=[];
    this.fallingTiles=[];
    this.events=[];
  }
}
